import { useMemo, useState, useCallback, useEffect } from 'react'
import {
  Button,
  Fa,
  TextInput,
  TableFilterDropdown,
} from '@eltoro-ui/components'
import {
  Campaignservicev1Campaign,
  Campaignservicev1OrderLine,
} from 'next-gen-sdk'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnFiltersState,
  ColumnDef,
} from '@tanstack/react-table'
import { getCampaignColumns, getOrderLineColumns } from './columns'
import { useDebounce } from '@eltoro-ui/hooks'
import { useReportHub } from '../../../Contexts/useReportHub'
import { TablePagination } from '../../TablePagination'

// Type for table data items - using union type for calculated stats
type OrderLineCalculatedStats = {
  ctr: number
  budgetServed: number
  budgetTotal: number
}

type CampaignCalculatedStats = {
  impressions: number
  clicks: number
  ctr: number
  budgetServed: number
  budgetTotal: number
}

type TableDataItem = (
  | Partial<Campaignservicev1OrderLine>
  | Partial<Campaignservicev1Campaign>
) & {
  orgName?: string
  orgId?: string
  calculatedStats?: OrderLineCalculatedStats | CampaignCalculatedStats
  stats?: any
}

// Extend React Table's ColumnMeta type
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  interface ColumnMeta<TData, TValue> {
    filterOn?: any
    removeFilter?: boolean
    removeSort?: boolean
  }
}

export const ReportHubTable = ({
  orderLineData,
  campaignData,
  view,
  includeChildOrgs,
  className,
  updateView,
}: {
  orderLineData: Partial<Campaignservicev1OrderLine>[] | undefined
  campaignData: Partial<Campaignservicev1Campaign>[] | undefined
  view: 'campaign' | 'orderLine'
  includeChildOrgs: boolean
  className?: string
  updateView: (view: 'campaign' | 'orderLine') => void
}) => {
  const { updateDisplayModal, clearTableFilters } = useReportHub()
  const [searchInput, setSearchInput] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const debouncedSearchInput = useDebounce(searchInput, 300)

  // Track loading state when search input differs from debounced value
  const isSearching = searchInput !== debouncedSearchInput

  // Set loading state when search input changes
  useEffect(() => {
    if (isSearching) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [isSearching])

  // Clear filters when view changes
  useEffect(() => {
    setSorting([])
    setColumnFilters([])
    setSearchInput('')
  }, [view])

  //Table Data
  const tableData: TableDataItem[] = useMemo(() => {
    return (view === 'orderLine'
      ? orderLineData
      : campaignData) as TableDataItem[]
  }, [view, orderLineData, campaignData])

  // Columns are now React Table compatible
  const columns = useMemo(() => {
    return view === 'orderLine'
      ? (getOrderLineColumns(includeChildOrgs) as ColumnDef<TableDataItem>[])
      : (getCampaignColumns(includeChildOrgs) as ColumnDef<TableDataItem>[])
  }, [view, includeChildOrgs])

  // Global filter function for search
  const globalFilterFn = useCallback(
    (row: any, columnId: string, value: string) => {
      const searchValue = value.toLowerCase()
      const name = row.original?.name?.toString().toLowerCase() || ''
      return name.includes(searchValue)
    },
    [],
  )

  // Create the react-table instance
  const table = useReactTable({
    data: tableData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn,
    state: {
      globalFilter: debouncedSearchInput,
      sorting,
      columnFilters,
    },
    onGlobalFilterChange: setSearchInput,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    enableGlobalFilter: true,
    enableSorting: true,
    enableColumnResizing: false, // Disable manual resizing but use size values
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 50,
      maxSize: 500,
    },
    enableColumnFilters: true,
  })

  // Adapter functions for TableFilterDropdown to work with React Table
  const handleSort = useCallback(
    (columnIndex: number, value?: 'desc' | 'asc') => {
      const column = table.getAllColumns()[columnIndex]
      if (!column) return

      if (value) {
        column.toggleSorting(value === 'desc')
      } else {
        column.toggleSorting()
      }
    },
    [table],
  )

  const handleFilter = useCallback(
    (
      columnIndex: number,
      path: string | string[],
      filterValues: string[] | number[],
    ) => {
      const column = table.getAllColumns()[columnIndex]
      if (!column) return

      // For React Table, we need to set the filter value
      if (filterValues.length === 0) {
        column.setFilterValue(undefined)
      } else if (filterValues.length === 1) {
        column.setFilterValue(filterValues[0])
      } else {
        column.setFilterValue(filterValues)
      }
    },
    [table],
  )

  const clearSort = useCallback(
    (columnIndex: number) => {
      const column = table.getAllColumns()[columnIndex]
      if (!column) return
      column.clearSorting()
    },
    [table],
  )

  // Convert React Table sorting state to TableFilterDropdown format
  const getCurrentSort = useCallback(
    (columnIndex: number): [number, 'asc' | 'desc'] | undefined => {
      const sortState = sorting.find((sort) => {
        const column = table.getAllColumns()[columnIndex]
        return column && sort.id === column.id
      })

      if (sortState) {
        return [columnIndex, sortState.desc ? 'desc' : 'asc']
      }
      return undefined
    },
    [sorting, table],
  )

  return (
    <section
      className={`ReportHubTableComponent bg-base border-tint-gray-300 rounded-lg border p-6 pt-4 ${
        className ?? ''
      }`}
    >
      <div className="ReportHubTableHeader mb-4 mt-0 flex flex-wrap items-center gap-x-2 gap-y-2">
        <div className="ReportHubTableViewToggles flex flex-1 gap-x-2 sm:flex-none">
          <Button
            className={`flex-1 px-4 py-2 transition-colors sm:flex-none ${
              view === 'campaign' ? 'bg-primary text-white font-bold' : ''
            }`}
            size="l"
            onClick={() => {
              clearTableFilters()
              updateView('campaign')
            }}
          >
            Campaigns
          </Button>
          <Button
            className={`flex-1 px-4 py-2 transition-colors sm:flex-none ${
              view === 'orderLine' ? 'bg-primary text-white font-bold' : ''
            }`}
            size="l"
            onClick={() => {
              clearTableFilters()
              updateView('orderLine')
            }}
          >
            Order lines
          </Button>
        </div>
        <Button
          className="flex-1 px-4 sm:flex-none"
          size="l"
          iconLeft={<Fa className="mr-1" icon="down-to-line" size={1} />}
          onClick={() => updateDisplayModal(true)}
        >
          Export
        </Button>
        <div className="ReportHubTableComponent__search-bar w-full pl-4 sm:ml-auto sm:w-1/2 lg:w-1/3 xl:w-1/4">
          <TextInput
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value)
            }}
            placeholder={`Search ${view === 'campaign' ? view : 'order line'}s`}
            iconRight={
              isLoading ? (
                <Fa
                  icon="spinner"
                  size={1}
                  className="text-primary animate-spin"
                />
              ) : searchInput ? (
                <button
                  onClick={() => {
                    setSearchInput('')
                  }}
                >
                  <Fa icon="xmark" size={1} className="text-secondary" />
                </button>
              ) : (
                <Fa icon="search" size={1} className="text-primary" />
              )
            }
          ></TextInput>
        </div>
      </div>
      <div
        className={`ReportHubTable transition-opacity duration-200 ${
          isLoading ? 'pointer-events-none opacity-50' : 'opacity-100'
        }`}
      >
        <div className="TableWrapper">
          <table className="Table w-full table-fixed">
            <thead className="TableHeader">
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <tr key={headerGroup.id} className="TableHeader__row">
                    {headerGroup.headers.map((header, headerIndex) => {
                      const { column } = header
                      const showSort = column.getCanSort()
                      const showFilter = column.getCanFilter()

                      return (
                        <th
                          key={header.id}
                          className="TableHeader__cell"
                          style={{
                            width: `${header.getSize()}px`,
                            minWidth: `${column.columnDef.minSize ?? 50}px`,
                            maxWidth: `${column.columnDef.maxSize ?? 500}px`,
                          }}
                        >
                          {showSort || showFilter ? (
                            <TableFilterDropdown
                              column={{
                                path: column.id,
                                label:
                                  typeof column.columnDef.header === 'string'
                                    ? column.columnDef.header
                                    : String(column.columnDef.header),
                                filterOn: column.columnDef.meta?.filterOn,
                                removeFilter:
                                  column.columnDef.meta?.removeFilter,
                                removeSort: column.columnDef.meta?.removeSort,
                              }}
                              columnIndex={headerIndex}
                              onFilter={handleFilter}
                              handleSort={handleSort}
                              sorted={getCurrentSort(headerIndex)}
                              setFiltered={() => {}} // No longer needed since we use React Table's built-in filter state
                              clearSort={clearSort}
                            >
                              <div className="TableHeader__label-container flex w-full items-center leading-none">
                                <span className="TableHeader__text flex-1">
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                                </span>
                                {/* Sort icon */}
                                {showSort && (
                                  <div className="TableSort__wrap flex items-center">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleSort(headerIndex)
                                      }}
                                      className="ButtonSort focus:ring-2"
                                      aria-label="Sort column"
                                    >
                                      <Fa
                                        icon="sort"
                                        size={2}
                                        type={
                                          getCurrentSort(headerIndex)?.[1] ===
                                          'asc'
                                            ? 'duotone'
                                            : getCurrentSort(
                                                headerIndex,
                                              )?.[1] === 'desc'
                                            ? 'duotone'
                                            : undefined
                                        }
                                        className={
                                          getCurrentSort(headerIndex)?.[1] ===
                                          'desc'
                                            ? 'rotate-180'
                                            : !getCurrentSort(headerIndex)
                                            ? 'text-tint-gray-300'
                                            : ''
                                        }
                                      />
                                    </button>
                                  </div>
                                )}
                                {/* Filter icon */}
                                {showFilter && (
                                  <button
                                    className={`ButtonFilter focus:ring-2 ${
                                      column.getFilterValue() !== undefined
                                        ? 'is-filtered'
                                        : ''
                                    }`}
                                    type="button"
                                    aria-label="Filter items"
                                  >
                                    <Fa icon="filter" size={1} />
                                  </button>
                                )}
                              </div>
                            </TableFilterDropdown>
                          ) : (
                            <div className="TableHeader__label-container flex items-center leading-none">
                              <span className="TableHeader__text flex-1">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              </span>
                            </div>
                          )}
                        </th>
                      )
                    })}
                  </tr>
                )
              })}
            </thead>
            <tbody className="Table__body">
              {table.getRowModel().rows.map((row, index) => {
                const rowBgClass =
                  index % 2 === 1 ? '!base-bg' : '!var(--base-gray)'
                return (
                  <tr key={row.id} className="TableRow">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className={`TableCell ${rowBgClass}`}>
                        <div
                          className={`TableCell__inner-container ${rowBgClass}`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div
        className={`ReportHubTablePaginationControls transition-opacity duration-200 ${
          isLoading ? 'pointer-events-none opacity-50' : 'opacity-100'
        }`}
      >
        <TablePagination
          pagination={{
            page: table.getState().pagination.pageIndex + 1,
            setPage: (page: number) => table.setPageIndex(page - 1),
            totalPages: table.getPageCount(),
            pageSize: table.getState().pagination.pageSize,
            setPageSize: (size: number) => table.setPageSize(size),
            loading: isLoading,
            totalItems: table.getFilteredRowModel().rows.length,
          }}
          pageSizeOptions={[
            { value: '10', label: '10 per page' },
            { value: '25', label: '25 per page' },
            { value: '50', label: '50 per page' },
          ]}
        />
      </div>
    </section>
  )
}
